<template>
  <AppPageContainer :custom="1600" :min-height="0">
    <AppPageHeader design="new" title="Observability" compact>
      <template #eyebrow>
        <AppStatusBadge v-if="dashboard" design="new" compact dot :tone="dashboard.partial ? 'amber' : 'green'" :label="dashboard.partial ? 'Partial data' : 'Metrics available'" />
      </template>
    </AppPageHeader>
    <main class="ui-page" :aria-busy="loading ? 'true' : 'false'">
      <AppFilterToolbar>
        <AppSelect v-model="periodPreset" design="new" input-id="observability-period" aria-label="Period" placeholder="Period" :options="periodOptions" :reduce="option => option.value" label="label" :clearable="false" @input="handlePeriodChange" />
        <template v-if="periodPreset === 'custom'">
          <AppDatePicker v-model="dateFrom" design="new" :input-attr="{ id: 'observability-date-from', 'aria-label': 'From' }" placeholder="From" :clearable="false" format="DD MMM YYYY" value-type="YYYY-MM-DD" @input="normalizeGranularity" />
          <AppDatePicker v-model="dateTo" design="new" :input-attr="{ id: 'observability-date-to', 'aria-label': 'To' }" placeholder="To" :clearable="false" format="DD MMM YYYY" value-type="YYYY-MM-DD" @input="normalizeGranularity" />
        </template>
        <AppSelect v-model="granularity" design="new" input-id="observability-granularity" aria-label="Granularity" placeholder="Granularity" :options="availableGranularityOptions" :reduce="option => option.value" label="label" :clearable="false" />
        <AppSelect v-if="dashboard && dashboard.resources.length" v-model="selectedService" design="new" input-id="observability-service" aria-label="Service" placeholder="Service" :options="serviceOptions" :reduce="option => option.value" label="label" :clearable="false" />
        <AppSelect v-if="dashboard" v-model="endpointSort" design="new" input-id="observability-endpoint-sort" aria-label="Rank by" placeholder="Rank by" :options="endpointSortOptions" :reduce="option => option.value" label="label" :clearable="false" @input="changeEndpointSort" />
        <template #actions>
          <AppIconButton label="Apply period" tooltip-placement="left" :disabled="loading || !canApplyPeriod" @click="applyPeriod"><i class="pi pi-check" aria-hidden="true" /></AppIconButton>
          <AppIconButton label="Refresh" tooltip-placement="left" :disabled="loading" @click="refreshDashboard"><i class="pi pi-refresh" aria-hidden="true" /></AppIconButton>
        </template>
        <template v-if="hasActiveFilters" #clear><AppClearFiltersButton design="new" icon-only :disabled="loading" @clear="clearFilters" /></template>
      </AppFilterToolbar>
      <p v-if="periodError" class="observability-field-error" role="alert">{{ periodError }}</p>
      <div v-if="dashboard" class="observability-applied-period">
        <span>{{ periodLabel }}</span>
        <span>{{ granularityLabel }} buckets · {{ dashboard.period.pointCount }} points maximum</span>
      </div>
      <AppErrorState v-if="error" :message="error" @retry="retryDashboard" />
    <AppCard
      v-if="dashboard && dashboard.partial"
      design="new"
      padding="compact"
      class="observability-state-card observability-state-card--warning"
    >
      <div class="observability-state" role="status">
        <div>
          <strong>Some metrics are not available</strong>
          <span>The available sections remain accurate for the selected period.</span>
        </div>
        <details v-if="dashboard.warnings.length" class="observability-warning-details">
          <summary>Technical details</summary>
          <div>{{ dashboard.warnings.join(' · ') }}</div>
        </details>
      </div>
    </AppCard>

    <AppContentSkeleton v-if="loading && !dashboard" layout="block" label="Loading observability dashboard" />

    <template v-if="dashboard">
      <section class="observability-metric-grid" aria-label="Selected period summary">
        <AppMetricCard
          v-for="metric in metricCards"
          :key="metric.label"
          design="new"
          padding="compact"
          :label="metric.label"
          :value="metric.value"
          :meta="metric.meta"
          :tone="metric.tone"
          :help="metric.help"
        >
          <span v-if="metric.latencyBand" :class="`observability-latency--${metric.latencyBand}`">{{ metric.value }}</span>
          <template v-else>{{ metric.value }}</template>
        </AppMetricCard>
      </section>

      <section class="observability-primary-grid" aria-label="Application performance charts">
        <AppCard
          design="new"
          title="Request traffic"
        >
          <highcharts v-if="hasTrafficData" :options="trafficChart" />
          <AppNoResults
            v-else
            design="new"
            small
            title="No request traffic"
            text=""
          />
        </AppCard>

        <AppCard
          design="new"
          title="HTTP errors"
        >
          <highcharts v-if="hasErrorData" :options="errorChart" />
          <AppNoResults
            v-else
            design="new"
            small
            title="No error data"
            text=""
          />
        </AppCard>

        <AppCard
          design="new"
          class="observability-latency-card"
          title="Latency percentiles"
        >
          <highcharts v-if="hasLatencyData" :options="latencyChart" />
          <AppNoResults
            v-else
            design="new"
            small
            title="No latency data"
            text=""
          />
        </AppCard>
      </section>

      <section class="observability-infrastructure-grid" aria-label="Infrastructure charts">
        <AppCard
          design="new"
          title="Host resources"
        >
          <template #actions>
            <div class="observability-card-summary">
              <span>CPU peak <strong>{{ formatMetric(dashboard.host.maximumCpuPercent, '%') }}</strong></span>
              <span>RAM peak <strong>{{ formatMetric(dashboard.host.maximumMemoryBytes, 'bytes') }}</strong></span>
            </div>
          </template>
          <highcharts v-if="hasHostData" :options="hostChart" />
          <AppNoResults
            v-else
            design="new"
            small
            title="No host metrics"
            text=""
          />
        </AppCard>

        <AppCard
          design="new"
          title="PostgreSQL connections"
        >
          <template #actions>
            <AppStatusBadge
              design="new"
              compact
              dot
              :tone="databaseTone"
              :label="databaseLabel"
            />
          </template>
          <highcharts v-if="hasDatabaseData" :options="databaseChart" />
          <AppNoResults
            v-else
            design="new"
            small
            title="No database metrics"
            text=""
          />
        </AppCard>
      </section>

      <AppCard
        design="new"
        class="observability-queue-card"
        title="Celery queues"
      >
        <template #actions>
          <div v-if="hasQueueRows" class="observability-card-summary">
            <span>Waiting now <strong>{{ formatCount(queueTotal) }}</strong></span>
          </div>
        </template>
        <template v-if="hasQueueRows">
          <highcharts v-if="queueNames.length" :options="queueChart" />
          <AppNoResults
            v-else
            design="new"
            small
            title="No queue backlog"
            text=""
          />
          <AppDataTable class="observability-queue-table" design="new" density="dense" :columns="queueColumns" :rows="queueRows" row-key="queue" min-width="1200px">
            <template #cell-queue="{ row }"><AppTableText :text="row.queue" /></template>
            <template #cell-currentLength="{ row }"><span :class="{ 'observability-queue--busy': row.currentLength > 0 }">{{ formatCount(row.currentLength) }}</span></template>
            <template #cell-maximumLength="{ row }">{{ formatCount(row.maximumLength) }}</template>
          </AppDataTable>
        </template>
        <AppNoResults
          v-else
          design="new"
          title="Queue metrics are not available"
          text="redis-exporter has not returned queue lengths. Other dashboard sections remain available."
        />
      </AppCard>

      <AppCard
        design="new"
        class="observability-rabbitmq-card"
        title="RabbitMQ"
      >
        <template #actions>
          <AppStatusBadge
            design="new"
            compact
            dot
            :tone="rabbitmqStatus.tone"
            :label="rabbitmqStatus.label"
          />
        </template>
        <p class="observability-rabbitmq-text">{{ rabbitmqStatus.text }}</p>
      </AppCard>

      <AppCard
        design="new"
        class="observability-service-card"
        title="Service resources"
      >

        <template v-if="selectedService && hasSelectedServiceData">
          <div class="observability-service-charts">
            <div>
              <h3>CPU usage</h3>
              <highcharts :options="serviceCpuChart" />
            </div>
            <div>
              <h3>Memory working set</h3>
              <highcharts :options="serviceMemoryChart" />
            </div>
          </div>
          <AppDataTable design="new" density="dense" :columns="resourceColumns" :rows="dashboard.resources" row-key="service" min-width="1200px">
            <template #cell-service="{ row }"><AppTableText :text="row.service" /></template>
            <template #cell-averageCpuPercent="{ row }">{{ formatMetric(row.averageCpuPercent, '%') }}</template>
            <template #cell-maximumCpuPercent="{ row }">{{ formatMetric(row.maximumCpuPercent, '%') }}</template>
            <template #cell-averageMemoryBytes="{ row }">{{ formatMetric(row.averageMemoryBytes, 'bytes') }}</template>
            <template #cell-maximumMemoryBytes="{ row }">{{ formatMetric(row.maximumMemoryBytes, 'bytes') }}</template>
          </AppDataTable>
        </template>
        <AppNoResults
          v-else
          design="new"
          title="Service metrics are not available"
          text="cAdvisor has not provided Docker Compose service labels. Other dashboard sections remain available."
        />
      </AppCard>

      <AppCard design="new" variant="table" header-density="compact" title="Top endpoints" class="observability-endpoints-card">
        <p class="observability-latency-legend">
          Latency:
          <span class="observability-latency--green">&lt; 200 ms</span>
          <span class="observability-latency--yellow">200–&lt;500 ms</span>
          <span class="observability-latency--orange">500–&lt;1000 ms</span>
          <span class="observability-latency--red">1000–&lt;3000 ms</span>
          <span class="observability-latency--burgundy">≥ 3000 ms</span>
        </p>
        <AppDataTable v-if="dashboard.topEndpoints.length" design="new" density="dense" :columns="endpointColumns" :rows="visibleTopEndpoints" :row-key="endpointKey" min-width="1200px">
          <template #cell-method="{ row }"><AppStatusBadge design="new" compact :label="row.method" tone="neutral" /></template>
          <template #cell-route="{ row }"><AppTableText :text="endpointText(row)" :tooltip="row.paths && row.paths.length > 1 ? 'Combined metrics for these paths: ' + endpointText(row) : ''" /></template>
          <template #cell-totalRequests="{ row }">{{ formatCount(row.totalRequests) }}</template>
          <template #cell-requestRate="{ row }">{{ formatMetric(row.requestRate, '') }}</template>
          <template #cell-serverErrorCount="{ row }">{{ formatCount(row.serverErrorCount) }}</template>
          <template #cell-serverErrorPercent="{ row }">{{ formatMetric(row.serverErrorPercent, '%') }}</template>
          <template #cell-p50Ms="{ row }"><span :class="`observability-latency--${latencyBand(row.p50Ms)}`">{{ formatMetric(row.p50Ms, 'ms') }}</span></template>
          <template #cell-p95Ms="{ row }"><span :class="`observability-latency--${latencyBand(row.p95Ms)}`">{{ formatMetric(row.p95Ms, 'ms') }}</span></template>
          <template #cell-p99Ms="{ row }"><span :class="`observability-latency--${latencyBand(row.p99Ms)}`">{{ formatMetric(row.p99Ms, 'ms') }}</span></template>
        </AppDataTable>
        <div v-if="dashboard.topEndpoints.length > 10" class="observability-endpoints-footer">
          <span>
            Showing {{ visibleTopEndpoints.length }} of {{ dashboard.topEndpoints.length }} endpoints
          </span>
          <AppButton
            design="new"
            theme="dark"
            size="small"
            @click.native="showAllEndpoints = !showAllEndpoints"
          >
            {{ showAllEndpoints ? 'Show top 10' : 'Show top 50' }}
          </AppButton>
        </div>
        <div v-if="!dashboard.topEndpoints.length" class="observability-endpoints-empty">
          <AppNoResults
            design="new"
            title="No endpoint activity"
            text=""
          />
        </div>
      </AppCard>

      <footer class="observability-footer">
        Last updated {{ lastUpdated }} · automatically refreshes every 60 seconds
      </footer>
    </template>
    </main>
  </AppPageContainer>
</template>

<script>
import {
  endOfDay,
  endOfWeek,
  format,
  parseISO,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks
} from 'date-fns'
import { Chart } from 'highcharts-vue'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import { getObservabilityDashboard } from '@/http'
import {
  activeQueues,
  availableGranularities,
  formatCount,
  formatMetric,
  hasValues,
  latencyBand,
  metricValue,
  pointsFor,
  rabbitmqStatus,
  sortQueueRows
} from './dashboardState'

const AUTO_REFRESH_INTERVAL = 60000
const MAXIMUM_POINTS = 1000
const CHART_COLORS = {
  blue: '#1769e0',
  blueSoft: '#8bb8f5',
  green: '#147a55',
  red: '#c53b4b',
  amber: '#9a5d00',
  ink: '#17243a',
  muted: '#697386',
  line: '#e6eaf0'
}
// Distinct enough to tell apart the handful of queues that are busy at once.
const QUEUE_COLORS = [
  '#1769e0', '#c53b4b', '#147a55', '#9a5d00', '#7c4dbd', '#0b8aa6', '#d1561f',
  '#4a5568', '#b0407a', '#5b8c00', '#8bb8f5', '#17243a', '#c9a227'
]
const ROLLING_SECONDS = {
  '1h': 60 * 60,
  '6h': 6 * 60 * 60,
  '24h': 24 * 60 * 60,
  '7d': 7 * 24 * 60 * 60,
  '30d': 30 * 24 * 60 * 60,
  '90d': 90 * 24 * 60 * 60
}
const GRANULARITY_OPTIONS = [
  { value: 'auto', label: 'Auto', seconds: null },
  { value: '1m', label: '1 minute', seconds: 60 },
  { value: '5m', label: '5 minutes', seconds: 5 * 60 },
  { value: '15m', label: '15 minutes', seconds: 15 * 60 },
  { value: '30m', label: '30 minutes', seconds: 30 * 60 },
  { value: '1h', label: '1 hour', seconds: 60 * 60 },
  { value: '6h', label: '6 hours', seconds: 6 * 60 * 60 },
  { value: '1d', label: '1 day', seconds: 24 * 60 * 60 }
]

export default {
  name: 'TechSupportObservabilityDashboard',
  components: { highcharts: Chart, AppContentSkeleton, AppErrorState, AppIconButton, AppTableText },
  data() {
    const today = format(new Date(), 'yyyy-MM-dd')
    return {
      queueColumns: [{ key: 'queue', label: 'Queue' }, { key: 'currentLength', label: 'Waiting now', align: 'right' }, { key: 'maximumLength', label: 'Peak in period', align: 'right' }],
      resourceColumns: [{ key: 'service', label: 'Service' }, { key: 'averageCpuPercent', label: 'Avg CPU', align: 'right' }, { key: 'maximumCpuPercent', label: 'Peak CPU', align: 'right' }, { key: 'averageMemoryBytes', label: 'Avg memory', align: 'right' }, { key: 'maximumMemoryBytes', label: 'Max memory', align: 'right' }],
      endpointColumns: [{ key: 'method', label: 'Method', width: '100px' }, { key: 'route', label: 'Endpoint', width: '300px' }, { key: 'totalRequests', label: 'Requests', align: 'right' }, { key: 'requestRate', label: 'Avg req/s', align: 'right' }, { key: 'serverErrorCount', label: '5xx', align: 'right' }, { key: 'serverErrorPercent', label: '5xx rate', align: 'right' }, { key: 'p50Ms', label: 'P50', align: 'right' }, { key: 'p95Ms', label: 'P95', align: 'right' }, { key: 'p99Ms', label: 'P99', align: 'right' }],
      dashboard: null,
      error: '',
      periodError: '',
      loading: false,
      refreshTimer: null,
      appliedParams: null,
      failedParams: null,
      periodPreset: '24h',
      granularity: 'auto',
      dateFrom: format(subDays(new Date(), 6), 'yyyy-MM-dd'),
      dateTo: today,
      selectedService: '',
      endpointSort: 'p95',
      showAllEndpoints: false,
      periodOptions: [
        { value: '1h', label: 'Last hour' },
        { value: '6h', label: 'Last 6 hours' },
        { value: '24h', label: 'Last 24 hours' },
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'this_week', label: 'This week' },
        { value: 'previous_week', label: 'Previous week' },
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: '90d', label: 'Last 90 days' },
        { value: 'custom', label: 'Custom dates' }
      ],
      endpointSortOptions: [
        { value: 'requests', label: 'Most requested' },
        { value: 'p95', label: 'Slowest P95' },
        { value: 'p99', label: 'Slowest P99' },
        { value: 'errors', label: 'Most 5xx errors' },
        { value: 'error_rate', label: 'Highest 5xx rate' }
      ]
    }
  },
  computed: {
    hasActiveFilters() {
      return this.periodPreset !== '24h' || this.granularity !== 'auto' || this.endpointSort !== 'p95' || Boolean(this.appliedParams && (this.appliedParams.range !== '24h' || this.appliedParams.granularity !== 'auto' || this.appliedParams.endpoint_sort !== 'p95')) || Boolean(this.dashboard && this.dashboard.resources.length && this.selectedService !== this.dashboard.resources[0].service)
    },
    visibleTopEndpoints() {
      if (!this.dashboard) return []
      return this.showAllEndpoints ? this.dashboard.topEndpoints : this.dashboard.topEndpoints.slice(0, 10)
    },
    selectedDurationSeconds() {
      if (ROLLING_SECONDS[this.periodPreset]) return ROLLING_SECONDS[this.periodPreset]
      const dates = this.selectedDateBounds()
      return dates ? Math.max(0, (dates.end - dates.start) / 1000) : 0
    },
    availableGranularityOptions() {
      return availableGranularities(
        this.selectedDurationSeconds,
        GRANULARITY_OPTIONS,
        MAXIMUM_POINTS
      )
    },
    canApplyPeriod() {
      if (this.periodPreset !== 'custom') return true
      return Boolean(this.dateFrom && this.dateTo && this.selectedDurationSeconds > 0)
    },
    periodLabel() {
      if (!this.dashboard) return ''
      const start = new Date(this.dashboard.period.start)
      const end = new Date(this.dashboard.period.end)
      return `${start.toLocaleString()} — ${end.toLocaleString()}`
    },
    granularityLabel() {
      if (!this.dashboard) return ''
      const option = GRANULARITY_OPTIONS.find(item => item.value === this.dashboard.period.granularity)
      return option ? option.label : this.dashboard.period.granularity
    },
    lastUpdated() {
      return new Date(this.dashboard.generatedAt).toLocaleString()
    },
    metricCards() {
      const summary = this.dashboard.summary || {}
      const serverErrors = metricValue(summary, 'server_error_count')
      const clientErrors = metricValue(summary, 'client_error_count')
      return [
        {
          label: 'Total requests',
          value: formatCount(metricValue(summary, 'total_requests')),
          meta: 'Complete selected period',
          tone: 'blue',
          help: 'All application responses recorded in the selected period.'
        },
        {
          label: 'Average request rate',
          value: formatMetric(metricValue(summary, 'average_request_rate'), 'req/s'),
          meta: 'Across the complete period',
          tone: 'neutral',
          help: 'Total requests divided by the selected period duration.'
        },
        {
          label: 'Peak request rate',
          value: formatMetric(metricValue(summary, 'peak_request_rate'), 'req/s'),
          meta: 'Highest rolling five-minute rate',
          tone: 'blue',
          help: 'The busiest rolling five-minute window in the selected period.'
        },
        {
          label: '5xx errors',
          value: formatMetric(metricValue(summary, 'server_error_percent'), '%'),
          meta: `${formatCount(serverErrors)} server errors`,
          tone: serverErrors ? 'red' : 'green',
          help: 'Server errors divided by all responses in the selected period.'
        },
        {
          label: '4xx responses',
          value: formatMetric(metricValue(summary, 'client_error_percent'), '%'),
          meta: `${formatCount(clientErrors)} client responses`,
          tone: clientErrors ? 'amber' : 'green',
          help: 'Client-side and validation responses divided by all responses.'
        },
        {
          label: 'P50 latency',
          latencyBand: latencyBand(metricValue(summary, 'latency_p50')),
          value: formatMetric(metricValue(summary, 'latency_p50'), 'ms'),
          meta: 'Median Django response',
          tone: 'green',
          help: 'Half of requests completed faster than this value.'
        },
        {
          label: 'P95 latency',
          latencyBand: latencyBand(metricValue(summary, 'latency_p95')),
          value: formatMetric(metricValue(summary, 'latency_p95'), 'ms'),
          meta: 'Slowest five percent',
          tone: 'amber',
          help: '95% of requests completed faster than this value.'
        },
        {
          label: 'P99 latency',
          latencyBand: latencyBand(metricValue(summary, 'latency_p99')),
          value: formatMetric(metricValue(summary, 'latency_p99'), 'ms'),
          meta: 'Tail latency',
          tone: 'red',
          help: '99% of requests completed faster than this value.'
        }
      ]
    },
    hasTrafficData() {
      return hasValues(this.dashboard.series, ['request_rate', 'request_rate_peak'])
    },
    hasErrorData() {
      return hasValues(this.dashboard.series, ['client_error_rate', 'server_error_rate'])
    },
    hasLatencyData() {
      return hasValues(this.dashboard.series, ['latency_p50', 'latency_p95', 'latency_p99'])
    },
    hasHostData() {
      return hasValues(this.dashboard.series, [
        'host_cpu_average',
        'host_cpu_maximum',
        'host_memory_average',
        'host_memory_maximum'
      ])
    },
    queueRows() {
      return sortQueueRows(this.dashboard.queues)
    },
    hasQueueRows() {
      return this.queueRows.length > 0
    },
    queueTotal() {
      return this.queueRows.reduce((total, row) => total + (row.currentLength || 0), 0)
    },
    queueNames() {
      return activeQueues(this.dashboard.series)
    },
    queueChart() {
      return this.chartOptions({
        yAxisTitle: 'Waiting messages',
        decimals: 0,
        series: this.queueNames.map((queue, index) => this.chartSeries(
          queue,
          'queue_length_maximum',
          QUEUE_COLORS[index % QUEUE_COLORS.length],
          labels => labels.queue === queue
        ))
      })
    },
    hasDatabaseData() {
      return hasValues(this.dashboard.series, [
        'postgres_connections_average',
        'postgres_connections_maximum'
      ])
    },
    databaseTone() {
      if (this.dashboard.database.up === true) return 'green'
      if (this.dashboard.database.up === false) return 'red'
      return 'neutral'
    },
    databaseLabel() {
      if (this.dashboard.database.up === true) {
        return `${formatMetric(this.dashboard.database.availabilityPercent, '%')} available`
      }
      if (this.dashboard.database.up === false) return 'Unavailable'
      return 'Unknown'
    },
    rabbitmqStatus() {
      return rabbitmqStatus(this.dashboard.rabbitmq)
    },
    serviceOptions() {
      return this.dashboard.resources.map(resource => ({
        value: resource.service,
        label: resource.service
      }))
    },
    selectedServiceFilter() {
      return labels => labels.service === this.selectedService
    },
    hasSelectedServiceData() {
      if (!this.selectedService) return false
      return hasValues(this.dashboard.series, [
        'container_cpu_average',
        'container_cpu_maximum',
        'container_memory_average',
        'container_memory_maximum'
      ], this.selectedServiceFilter)
    },
    trafficChart() {
      return this.chartOptions({
        yAxisTitle: 'Requests per second',
        series: [
          this.chartSeries('Average req/s', 'request_rate', CHART_COLORS.blue),
          {
            ...this.chartSeries('Peak 5m req/s', 'request_rate_peak', CHART_COLORS.blueSoft),
            dashStyle: 'ShortDash'
          }
        ]
      })
    },
    errorChart() {
      return this.chartOptions({
        yAxisTitle: 'Responses (%)',
        series: [
          this.chartSeries('4xx rate', 'client_error_rate', CHART_COLORS.amber),
          this.chartSeries('5xx rate', 'server_error_rate', CHART_COLORS.red)
        ]
      })
    },
    latencyChart() {
      return this.chartOptions({
        yAxisTitle: 'Milliseconds',
        series: [
          this.chartSeries('P50', 'latency_p50', CHART_COLORS.green),
          this.chartSeries('P95', 'latency_p95', CHART_COLORS.amber),
          this.chartSeries('P99', 'latency_p99', CHART_COLORS.red)
        ]
      })
    },
    hostChart() {
      return this.chartOptions({
        yAxisTitle: 'Usage (%)',
        maximum: 100,
        series: [
          this.chartSeries('Average CPU', 'host_cpu_average', CHART_COLORS.blue),
          {
            ...this.chartSeries('Maximum CPU', 'host_cpu_maximum', CHART_COLORS.blueSoft),
            dashStyle: 'ShortDash'
          },
          this.chartSeries('Average memory', 'host_memory_average', CHART_COLORS.green),
          {
            ...this.chartSeries('Maximum memory', 'host_memory_maximum', CHART_COLORS.amber),
            dashStyle: 'ShortDash'
          }
        ]
      })
    },
    databaseChart() {
      return this.chartOptions({
        yAxisTitle: 'Connections',
        series: [
          this.chartSeries('Average connections', 'postgres_connections_average', CHART_COLORS.blue),
          {
            ...this.chartSeries('Maximum connections', 'postgres_connections_maximum', CHART_COLORS.amber),
            dashStyle: 'ShortDash'
          }
        ]
      })
    },
    serviceCpuChart() {
      return this.chartOptions({
        height: 260,
        yAxisTitle: 'CPU (%)',
        series: [
          this.chartSeries(
            'Average CPU',
            'container_cpu_average',
            CHART_COLORS.blue,
            this.selectedServiceFilter
          ),
          {
            ...this.chartSeries(
              'Maximum CPU',
              'container_cpu_maximum',
              CHART_COLORS.amber,
              this.selectedServiceFilter
            ),
            dashStyle: 'ShortDash'
          }
        ]
      })
    },
    serviceMemoryChart() {
      return this.chartOptions({
        height: 260,
        yAxisTitle: 'Memory (MiB)',
        series: [
          this.chartSeries(
            'Average memory',
            'container_memory_average',
            CHART_COLORS.green,
            this.selectedServiceFilter,
            value => value / 1024 / 1024
          ),
          {
            ...this.chartSeries(
              'Maximum memory',
              'container_memory_maximum',
              CHART_COLORS.amber,
              this.selectedServiceFilter,
              value => value / 1024 / 1024
            ),
            dashStyle: 'ShortDash'
          }
        ]
      })
    }
  },
  created() {
    this.loadDashboard(this.buildQueryParams())
    this.refreshTimer = window.setInterval(this.refreshDashboard, AUTO_REFRESH_INTERVAL)
  },
  beforeDestroy() {
    window.clearInterval(this.refreshTimer)
  },
  methods: {
    endpointKey(endpoint) { return `${endpoint.method}-${endpoint.route}` },
    endpointText(endpoint) { return endpoint.paths && endpoint.paths.length ? endpoint.paths.join(' · ') : endpoint.route },
    clearFilters() {
      this.periodPreset = '24h'
      this.granularity = 'auto'
      this.endpointSort = 'p95'
      this.selectedService = this.dashboard && this.dashboard.resources.length ? this.dashboard.resources[0].service : ''
      this.periodError = ''
      return this.loadDashboard(this.buildQueryParams())
    },
    formatCount,
    formatMetric,
    latencyBand,
    selectedDateBounds() {
      const now = new Date()
      if (this.periodPreset === 'today') {
        return { start: startOfDay(now), end: now }
      }
      if (this.periodPreset === 'yesterday') {
        const yesterday = subDays(now, 1)
        return { start: startOfDay(yesterday), end: endOfDay(yesterday) }
      }
      if (this.periodPreset === 'this_week') {
        return { start: startOfWeek(now, { weekStartsOn: 1 }), end: now }
      }
      if (this.periodPreset === 'previous_week') {
        const previousWeek = subWeeks(now, 1)
        return {
          start: startOfWeek(previousWeek, { weekStartsOn: 1 }),
          end: endOfWeek(previousWeek, { weekStartsOn: 1 })
        }
      }
      if (this.periodPreset === 'custom' && this.dateFrom && this.dateTo) {
        const start = startOfDay(parseISO(this.dateFrom))
        const selectedEnd = parseISO(this.dateTo)
        let end = endOfDay(selectedEnd)
        if (format(selectedEnd, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd')) end = now
        return { start, end }
      }
      return null
    },
    buildQueryParams() {
      const params = {
        granularity: this.granularity,
        endpoint_sort: this.endpointSort
      }
      if (ROLLING_SECONDS[this.periodPreset]) {
        params.range = this.periodPreset
        return params
      }
      const dates = this.selectedDateBounds()
      if (dates) {
        params.start = dates.start.toISOString()
        params.end = dates.end.toISOString()
      }
      return params
    },
    normalizeGranularity() {
      this.$nextTick(() => {
        if (!this.availableGranularityOptions.some(option => option.value === this.granularity)) {
          this.granularity = 'auto'
        }
      })
    },
    handlePeriodChange() {
      this.periodError = ''
      this.normalizeGranularity()
    },
    applyPeriod() {
      this.periodError = ''
      if (!this.canApplyPeriod) {
        this.periodError = 'Choose a valid start and end date.'
        return Promise.resolve()
      }
      return this.loadDashboard(this.buildQueryParams())
    },
    refreshDashboard() {
      return this.loadDashboard(this.appliedParams || this.buildQueryParams())
    },
    retryDashboard() {
      return this.loadDashboard(this.failedParams || this.appliedParams || this.buildQueryParams())
    },
    changeEndpointSort() {
      if (!this.appliedParams) return
      this.loadDashboard({ ...this.appliedParams, endpoint_sort: this.endpointSort })
    },
    loadDashboard(params) {
      if (this.loading) return Promise.resolve()
      this.loading = true
      this.error = ''
      return getObservabilityDashboard(params)
        .then(response => {
          this.dashboard = response.data
          this.appliedParams = { ...params }
          this.failedParams = null
          this.endpointSort = response.data.endpointSort
          this.showAllEndpoints = false
          const availableServices = response.data.resources.map(resource => resource.service)
          if (!availableServices.includes(this.selectedService)) {
            this.selectedService = availableServices[0] || ''
          }
        })
        .catch(error => {
          this.failedParams = { ...params }
          this.error = 'Business services are not affected. Try refreshing the metrics.'
          if (error.response && error.response.data && error.response.data.details) {
            this.error = error.response.data.details
          }
        })
        .finally(() => { this.loading = false })
    },
    chartOptions({ yAxisTitle, series, maximum, height = 300, decimals }) {
      const valueDecimals = decimals !== undefined ? decimals : (this.dashboard.meta ? this.dashboard.meta.precision : 1)
      return {
        accessibility: { enabled: false },
        chart: {
          height,
          backgroundColor: 'transparent',
          spacing: [12, 4, 8, 4],
          style: { fontFamily: 'CoreSansCR', fontSize: '14px' }
        },
        colors: Object.values(CHART_COLORS),
        credits: { enabled: false },
        legend: {
          align: 'left',
          itemStyle: { color: CHART_COLORS.ink, fontSize: '12px', fontWeight: '500' },
          itemHoverStyle: { color: CHART_COLORS.blue },
          symbolRadius: 4,
          verticalAlign: 'bottom'
        },
        plotOptions: {
          series: {
            animation: false,
            lineWidth: 2,
            marker: { enabled: false },
            states: { hover: { lineWidthPlus: 0 } }
          }
        },
        title: { text: '' },
        tooltip: {
          shared: true,
          valueDecimals,
          xDateFormat: '%e %b %Y, %H:%M',
          borderColor: CHART_COLORS.line,
          borderRadius: 8,
          shadow: false,
          style: { color: CHART_COLORS.ink, fontSize: '13px' }
        },
        xAxis: {
          type: 'datetime',
          lineColor: CHART_COLORS.line,
          tickColor: CHART_COLORS.line,
          labels: { style: { color: CHART_COLORS.muted, fontSize: '12px' } }
        },
        yAxis: {
          title: { text: yAxisTitle, style: { color: CHART_COLORS.muted, fontSize: '12px' } },
          min: 0,
          max: maximum,
          gridLineColor: CHART_COLORS.line,
          labels: { style: { color: CHART_COLORS.muted, fontSize: '12px' } }
        },
        series
      }
    },
    chartSeries(name, key, color, labelFilter, transform) {
      return {
        name,
        color,
        data: pointsFor(
          this.dashboard.series,
          key,
          labelFilter,
          transform || (value => value)
        )
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.observability-rabbitmq-text { margin: 0; }
.observability-queue-table { margin-top: $ui-space-16; }
.observability-queue--busy { color: $ui-ink; font-weight: 600; }
.observability-field-error { margin: 0; color: $ui-red; font-size: $ui-font-size-body; }
.observability-applied-period { display: flex; justify-content: space-between; flex-wrap: wrap; gap: $ui-space-12; color: $ui-muted; font-size: $ui-font-size-metadata; }
.observability-state-card--warning { border-color: rgba($ui-amber, .35); background-color: $ui-amber-soft; }
.observability-state { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-16; }
.observability-state > div { display: grid; gap: $ui-space-4; }
.observability-state strong { color: $ui-ink; font: 600 $ui-font-size-body $ui-font-heading; }
.observability-state span, .observability-warning-details { color: $ui-muted; font-size: $ui-font-size-body; line-height: 1.45; }
.observability-warning-details { max-width: 520px; text-align: right; }
.observability-warning-details summary { color: $ui-amber; cursor: pointer; font-weight: 600; }
.observability-warning-details div { margin-top: $ui-space-4; overflow-wrap: anywhere; }
.observability-metric-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: $ui-space-16; }
.observability-primary-grid, .observability-infrastructure-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-16; }
.observability-primary-grid > *, .observability-infrastructure-grid > * { min-width: 0; }
.observability-latency-card { grid-column: 1 / -1; }
.observability-card-summary { display: flex; flex-wrap: wrap; gap: $ui-space-8 $ui-space-16; color: $ui-muted; font-size: $ui-font-size-metadata; }
.observability-card-summary strong { color: $ui-ink; font-family: $ui-font-heading; font-weight: 600; }
.observability-service-charts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-24; margin-bottom: $ui-space-20; }
.observability-service-charts h3 { margin: 0 0 $ui-space-8; color: $ui-ink; font: 600 $ui-font-size-body $ui-font-heading; }
.observability-latency-legend { display: flex; flex-wrap: wrap; gap: $ui-space-12; margin: 0; padding: $ui-space-12 $ui-space-16; color: $ui-muted; font-size: $ui-font-size-metadata; }
.observability-latency--green { color: $ui-green; }
.observability-latency--yellow { color: $ui-amber; }
.observability-latency--orange { color: #b45309; }
.observability-latency--red { color: $ui-red; }
.observability-latency--burgundy { color: #800020; }
.observability-endpoints-empty { padding: $ui-space-16; }
.observability-endpoints-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: $ui-space-16; padding: $ui-space-12 $ui-space-16; border-top: 1px solid $ui-line; color: $ui-muted; font-size: $ui-font-size-metadata; }
.observability-footer { color: $ui-muted; font-size: $ui-font-size-metadata; line-height: 18px; text-align: right; }
@media (max-width: 1180px) { .observability-metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 760px) {
  .observability-primary-grid, .observability-infrastructure-grid, .observability-service-charts { grid-template-columns: 1fr; }
  .observability-latency-card { grid-column: auto; }
  .observability-state { align-items: stretch; flex-direction: column; }
  .observability-warning-details { max-width: none; text-align: left; }
}
@media (max-width: 600px) { .observability-metric-grid { grid-template-columns: 1fr; } }
</style>
